import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "../stores/auth";
import { usePermissionStore } from "../stores/permissionStore";
import { requiredRoutePermissions } from "../config/permissions";

function createOnList(path) {
  return (to) => ({ path, query: { ...to.query, create: "1" } });
}

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login.vue"),
    meta: { requiresGuest: true },
  },
  {
    path: "/",
    name: "Dashboard",
    component: () => import("../layouts/DashboardLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "Dashboard4",
        component: () => import("../views/Dashboard4.vue"),
      },
      {
        path: "dashboard2",
        name: "Dashboard2",
        component: () => import("../views/Dashboard2.vue"),
      },
      {
        path: "dashboard3",
        name: "Dashboard3",
        component: () => import("../views/Dashboard3.vue"),
      },
      {
        path: "transactions",
        name: "Transactions",
        component: () => import("../views/Transactions.vue"),
      },
      //subscription plan routes
      {
        path: "subscription-plan",
        name: "Subscription Plan",
        component: () =>
          import("../views/finance/containers/SubscriptionPlans/SubscriptionPlanList.vue"),
      },

      {
        path: "subscription-plan/create",
        name: "Subscription Plan Create",
        component: () =>
          import("../views/finance/containers/SubscriptionPlans/SubscriptionPlanForm.vue"),
      },
      {
        path: "subscription-plan/edit/:id",
        name: "Subscription Plan Edit",
        component: () =>
          import("../views/finance/containers/SubscriptionPlans/SubscriptionPlanForm.vue"),
        props: true,
      },
      //subscription routes
      {
        path: "subscriptions",
        name: "Subscriptions",
        component: () =>
          import("../views/finance/containers/Subscriptions/SubscriptionList.vue"),
      },
      {
        path: "subscriptions/create",
        name: "Subscription Create",
        component: () =>
          import("../views/finance/containers/Subscriptions/SubscriptionForm.vue"),
      },
      {
        path: "subscriptions/edit/:id",
        name: "Subscription Edit",
        component: () =>
          import("../views/finance/containers/Subscriptions/SubscriptionForm.vue"),
        props: true,
      },
      //invoice routes
      {
        path: "invoices",
        name: "Invoices",
        component: () =>
          import("../views/finance/containers/Invoices/InvoiceList.vue"),
      },
      {
        path: "invoices/create",
        name: "Invoice Create",
        component: () =>
          import("../views/finance/containers/Invoices/InvoiceForm.vue"),
      },
      {
        path: "invoices/edit/:id",
        name: "Invoice Edit",
        component: () =>
          import("../views/finance/containers/Invoices/InvoiceForm.vue"),
        props: true,
      },
      {
        path: "invoices/payment/:id",
        name: "Invoice Payment",
        component: () =>
          import("../views/finance/containers/Invoices/PaymentForm.vue"),
        props: true,
      },
      //company routes
      {
        path: "companies/customers",
        name: "Customer Companies",
        component: () =>
          import("../views/finance/containers/Companies/CompanyList.vue"),
      },
      {
        path: "companies/customers/create",
        name: "Create Customer Company",
        redirect: createOnList("/companies/customers"),
      },
      {
        path: "companies/customers/edit/:id",
        name: "Edit Customer Company",
        component: () =>
          import("../views/finance/containers/Companies/CompanyForm.vue"),
        props: true,
      },
      {
        path: "companies/suppliers",
        name: "Supplier Companies",
        component: () =>
          import("../views/finance/containers/Companies/CompanyList.vue"),
      },
      {
        path: "companies/suppliers/create",
        name: "Create Supplier Company",
        redirect: createOnList("/companies/suppliers"),
      },
      {
        path: "companies/suppliers/edit/:id",
        name: "Edit Supplier Company",
        component: () =>
          import("../views/finance/containers/Companies/CompanyForm.vue"),
        props: true,
      },
      {
        path: "companies",
        name: "Companies",
        component: () =>
          import("../views/finance/containers/Companies/CompanyList.vue"),
      },
      {
        path: "finance/hierarchy",
        name: "Enterprise Hierarchy",
        component: () => import("../views/HierarchyDemo.vue"),
      },
      {
        path: "finance/chart-of-accounts/hierarchy",
        name: "Chart of Accounts Hierarchy",
        component: () => import("../views/ChartOfAccountsHierarchy.vue"),
      },
      {
        path: "finance/ledger",
        name: "General Ledger",
        component: () => import("../views/finance/containers/Ledger/LedgerView.vue"),
      },
      {
        path: "finance/chart-of-accounts/:accountId/ledger",
        name: "Chart of Account Ledger",
        component: () => import("../views/finance/containers/Ledger/LedgerView.vue"),
        props: true,
      },

      {
        path: "company/create",
        name: "Company Create",
        component: () =>
          import("../views/finance/containers/Companies/CompanyForm.vue"),
      },
      {
        path: "company/edit/:id",
        name: "Company Edit",
        component: () =>
          import("../views/finance/containers/Companies/CompanyForm.vue"),
        props: true,
      },
      // Intercompany Transactions Routes
      {
        path: "finance/intercompany-transactions",
        name: "Intercompany Transactions",
        component: () =>
          import("../views/finance/containers/IntercompanyTransactions/IntercompanyTransactionList.vue"),
      },
      {
        path: "finance/intercompany-transactions/create",
        name: "Create Intercompany Transaction",
        component: () =>
          import("../views/finance/containers/IntercompanyTransactions/IntercompanyTransactionForm.vue"),
      },
      // AR Invoices Routes
      {
        path: "finance/ar-invoices",
        name: "Invoices",
        component: () =>
          import("../views/finance/containers/ArInvoices/ArInvoiceList.vue"),
      },
      {
        path: "finance/ar-invoices/create",
        name: "Create AR Invoice",
        component: () =>
          import("../views/finance/containers/ArInvoices/ArInvoiceForm.vue"),
      },
      {
        path: "finance/ar-invoices/edit/:id",
        name: "Edit AR Invoice",
        component: () =>
          import("../views/finance/containers/ArInvoices/ArInvoiceForm.vue"),
        props: true,
      },
      {
        path: "finance/ap-bills",
        name: "Supplier Bills",
        component: () => import("../views/finance/containers/ApBills/ApBillList.vue"),
      },
      {
        path: "finance/ap-bills/create",
        name: "Create Supplier Bill",
        component: () => import("../views/finance/containers/ApBills/ApBillForm.vue"),
      },
      {
        path: "finance/ap-bills/:id",
        name: "Supplier Bill",
        component: () => import("../views/finance/containers/ApBills/ApBillForm.vue"),
        props: true,
      },
      {
        path: "finance/ar-receipts",
        name: "AR Receipts",
        component: () =>
          import("../views/finance/containers/ArReceipts/ArReceiptList.vue"),
      },
      {
        path: "finance/ar-receipts/create",
        name: "Receive Payment",
        component: () =>
          import("../views/finance/containers/ArReceipts/ArReceiptForm.vue"),
      },
      {
        path: "finance/ar-receipts/:id",
        name: "AR Receipt",
        component: () =>
          import("../views/finance/containers/ArReceipts/ArReceiptForm.vue"),
        props: true,
      },
      // Expenses � direct expense and bill payment on one screen
      {
        path: "finance/expenses",
        name: "Expenses",
        component: () => import("../views/finance/containers/Expenses/ExpenseList.vue"),
      },
      {
        path: "finance/expenses/check",
        name: "Check Expenses",
        component: () => import("../views/finance/containers/Expenses/CheckExpenseList.vue"),
      },
      {
        path: "finance/expenses/online",
        name: "Online Expenses",
        component: () => import("../views/finance/containers/Expenses/OnlineExpenseList.vue"),
      },
      {
        path: "finance/expenses/tt",
        name: "TT Expenses",
        component: () => import("../views/finance/containers/Expenses/TtExpenseList.vue"),
      },
      {
        path: "finance/expenses/create",
        name: "Create Expense",
        component: () => import("../views/finance/containers/Expenses/ExpenseDocumentForm.vue"),
      },
      {
        path: "finance/expenses/pay-bills",
        redirect: "/finance/expenses/create",
      },
      {
        path: "finance/expenses/new/:kind",
        redirect: "/finance/expenses/create",
      },
      {
        path: "finance/expenses/:id",
        name: "Expense",
        component: () => import("../views/finance/containers/Expenses/ExpenseDocumentForm.vue"),
      },
      // Company Layers Routes
      {
        path: "finance/company-layers",
        name: "Company Layers",
        component: () =>
          import("../views/finance/containers/CompanyLayers/CompanyLayerList.vue"),
      },
      {
        path: "finance/company-layers/create",
        name: "Create Company Layer",
        component: () =>
          import("../views/finance/containers/CompanyLayers/CompanyLayerForm.vue"),
      },
      {
        path: "finance/company-layers/edit/:id",
        name: "Edit Company Layer",
        component: () =>
          import("../views/finance/containers/CompanyLayers/CompanyLayerForm.vue"),
        props: true,
      },
      // Company Categories Routes
      {
        path: "finance/company-categories",
        name: "Company Categories",
        component: () =>
          import("../views/finance/containers/CompanyCategories/CompanyCategoryList.vue"),
      },
      {
        path: "finance/company-categories/create",
        name: "Create Company Category",
        component: () =>
          import("../views/finance/containers/CompanyCategories/CompanyCategoryForm.vue"),
      },
      {
        path: "finance/company-categories/edit/:id",
        name: "Edit Company Category",
        component: () =>
          import("../views/finance/containers/CompanyCategories/CompanyCategoryForm.vue"),
        props: true,
      },
      // Company Classes Routes
      {
        path: "finance/customer-classes",
        name: "Company Classes",
        component: () =>
          import("../views/finance/containers/CustomerClasses/CustomerClassList.vue"),
      },
      {
        path: "finance/customer-classes/create",
        name: "Create Company Class",
        component: () =>
          import("../views/finance/containers/CustomerClasses/CustomerClassForm.vue"),
      },
      {
        path: "finance/customer-classes/edit/:id",
        name: "Edit Company Class",
        component: () =>
          import("../views/finance/containers/CustomerClasses/CustomerClassForm.vue"),
        props: true,
      },
      // Finance Companies Routes
      {
        path: "finance/finance-companies",
        name: "Finance Companies",
        component: () =>
          import("../views/finance/containers/FinanceCompanies/FinanceCompanyList.vue"),
      },
      {
        path: "finance/finance-companies/create",
        name: "Create Finance Company",
        component: () =>
          import("../views/finance/containers/FinanceCompanies/FinanceCompanyForm.vue"),
      },
      {
        path: "finance/finance-companies/edit/:id",
        name: "Edit Finance Company",
        component: () =>
          import("../views/finance/containers/FinanceCompanies/FinanceCompanyForm.vue"),
        props: true,
      },
      //accounts routes
      {
        path: "accounts",
        name: "Accounts",
        component: () => import("../views/Accounts.vue"),
      },
      {
        path: "finance/companies/:companyId/accounts",
        name: "Company Accounts",
        component: () => import("../views/CompanyAccounts.vue"),
        props: true,
      },
      {
        path: "finance/companies/:companyId/bank-details",
        name: "Bank Details",
        component: () => import("../views/finance/containers/BankDetails/BankDetailList.vue"),
        props: true,
      },
      // feature categories routes
      {
        path: "feature-categories",
        name: "Feature Categories",
        component: () =>
          import("../views/finance/containers/FeatureCategories/FeatureCategoryList.vue"),
      },
      {
        path: "feature-categories/create",
        name: "Feature Category Create",
        component: () =>
          import("../views/finance/containers/FeatureCategories/FeatureCategoryForm.vue"),
      },
      {
        path: "feature-categories/edit/:id",
        name: "Feature Category Edit",
        component: () =>
          import("../views/finance/containers/FeatureCategories/FeatureCategoryForm.vue"),
        props: true,
      },
      // features routes
      {
        path: "features",
        name: "Features",
        component: () =>
          import("../views/finance/containers/Features/FeatureList.vue"),
      },
      {
        path: "features/create",
        name: "Feature Create",
        component: () =>
          import("../views/finance/containers/Features/FeatureForm.vue"),
      },
      {
        path: "features/import",
        name: "Feature Import",
        component: () =>
          import("../views/finance/containers/Features/FeatureImport.vue"),
      },
      {
        path: "features/edit/:id",
        name: "Feature Edit",
        component: () =>
          import("../views/finance/containers/Features/FeatureForm.vue"),
        props: true,
      },
      // add-ons routes
      {
        path: "add-ons",
        name: "Add-ons",
        component: () =>
          import("../views/finance/containers/AddOns/AddOnList.vue"),
      },
      {
        path: "add-ons/create",
        name: "Add-on Create",
        component: () =>
          import("../views/finance/containers/AddOns/AddOnForm.vue"),
      },
      {
        path: "add-ons/edit/:id",
        name: "Add-on Edit",
        component: () =>
          import("../views/finance/containers/AddOns/AddOnForm.vue"),
        props: true,
      },
      // charge type routes
      {
        path: "charge-types",
        name: "Charge Types",
        component: () =>
          import("../views/finance/containers/ChargeTypes/ChargeTypeList.vue"),
      },
      {
        path: "charge-types/create",
        name: "Charge Type Create",
        redirect: createOnList("/charge-types"),
      },
      {
        path: "charge-types/edit/:id",
        name: "Charge Type Edit",
        component: () =>
          import("../views/finance/containers/ChargeTypes/ChargeTypeForm.vue"),
        props: true,
      },
      {
        path: "budgets",
        name: "Budgets",
        component: () => import("../views/Budgets.vue"),
      },
      {
        path: "investments",
        name: "Investments",
        component: () => import("../views/Investments.vue"),
      },
      {
        path: "taxes",
        name: "Taxes",
        component: () => import("../views/Taxes.vue"),
      },
      {
        path: "reports",
        name: "Reports",
        component: () => import("../views/Reports.vue"),
      },
      {
        path: "finance/reports/trial-balance",
        name: "TrialBalanceReport",
        component: () =>
          import("../views/finance/containers/Reports/TrialBalanceView.vue"),
      },
      {
        path: "reports/trial-balance",
        redirect: "/finance/reports/trial-balance",
      },
      {
        path: "finance/reports/profit-and-loss",
        name: "ProfitAndLossReport",
        component: () =>
          import("../views/finance/containers/Reports/ProfitAndLossView.vue"),
      },
      {
        path: "reports/profit-and-loss",
        redirect: "/finance/reports/profit-and-loss",
      },
      {
        path: "finance/reports/ar-ageing-summary",
        name: "ArAgeingSummaryReport",
        component: () =>
          import("../views/finance/containers/Reports/ArAgeingSummaryView.vue"),
      },
      {
        path: "reports/ar-ageing-summary",
        redirect: "/finance/reports/ar-ageing-summary",
      },
      {
        path: "finance/reports/ap-ageing-summary",
        name: "ApAgeingSummaryReport",
        component: () =>
          import("../views/finance/containers/Reports/ApAgeingSummaryView.vue"),
      },
      {
        path: "reports/ap-ageing-summary",
        redirect: "/finance/reports/ap-ageing-summary",
      },
      {
        path: "finance/reports/balance-sheet",
        name: "BalanceSheetReport",
        component: () =>
          import("../views/finance/containers/Reports/BalanceSheetView.vue"),
      },
      {
        path: "reports/balance-sheet",
        redirect: "/finance/reports/balance-sheet",
      },
      {
        path: "finance/reports/chart-of-accounts",
        name: "ChartOfAccountsReport",
        component: () =>
          import("../views/finance/containers/Reports/ChartOfAccountsReportView.vue"),
      },
      {
        path: "reports/chart-of-accounts",
        redirect: "/finance/reports/chart-of-accounts",
      },
      {
        path: "finance/reports/sub-ledger",
        name: "SubLedgerReport",
        component: () =>
          import("../views/finance/containers/Reports/SubLedgerReportView.vue"),
      },
      {
        path: "reports/sub-ledger",
        redirect: "/finance/reports/sub-ledger",
      },
      {
        path: "finance/reports/opening-balances",
        name: "OpeningBalanceReport",
        component: () =>
          import("../views/finance/containers/Reports/OpeningBalanceReportView.vue"),
      },
      {
        path: "reports/opening-balances",
        redirect: "/finance/reports/opening-balances",
      },
      {
        path: "finance/reports/sales-performance",
        name: "SalesPerformanceReport",
        component: () =>
          import("../views/finance/containers/Reports/SalesPerformanceReportView.vue"),
      },
      {
        path: "reports/sales-performance",
        redirect: "/finance/reports/sales-performance",
      },
      {
        path: "finance/reports/salesperson",
        name: "SalespersonReport",
        component: () =>
          import("../views/finance/containers/Reports/SalespersonReportView.vue"),
      },
      {
        path: "reports/salesperson",
        redirect: "/finance/reports/salesperson",
      },
      {
        path: "finance/reports/period-comparison",
        name: "PeriodComparisonReport",
        component: () =>
          import("../views/finance/containers/Reports/PeriodComparisonReportView.vue"),
      },
      {
        path: "reports/period-comparison",
        redirect: "/finance/reports/period-comparison",
      },
      {
        path: "finance/reports/bank-reconciliation",
        name: "BankReconciliationReport",
        component: () =>
          import("../views/finance/containers/Reports/BankReconciliationReportView.vue"),
      },
      {
        path: "reports/bank-reconciliation",
        redirect: "/finance/reports/bank-reconciliation",
      },
      {
        path: "finance/reports/tax-turnover",
        name: "TaxTurnoverReport",
        component: () =>
          import("../views/finance/containers/Reports/TaxTurnoverReportView.vue"),
      },
      {
        path: "reports/tax-turnover",
        redirect: "/finance/reports/tax-turnover",
      },
      {
        path: "finance/reports/tax-reconciliation",
        name: "TaxReconciliationReport",
        component: () => import("../views/finance/containers/Reports/TaxReconciliationView.vue"),
      },
      {
        path: "reports/tax-reconciliation",
        redirect: "/finance/reports/tax-reconciliation",
      },
      {
        path: "finance/reports/cost-centre-profit-and-loss",
        name: "CostCentreProfitAndLossReport",
        component: () =>
          import("../views/finance/containers/Reports/CostCentreProfitAndLossView.vue"),
      },
      {
        path: "reports/cost-centre-profit-and-loss",
        redirect: "/finance/reports/cost-centre-profit-and-loss",
      },
      {
        path: "finance/reports/project-profitability",
        name: "ProjectProfitabilityReport",
        component: () =>
          import("../views/finance/containers/Reports/ProjectProfitabilityView.vue"),
      },
      {
        path: "reports/project-profitability",
        redirect: "/finance/reports/project-profitability",
      },
      {
        path: "finance/reports/fixed-assets",
        name: "FixedAssetRegisterReport",
        component: () => import("../views/finance/containers/Reports/FixedAssetRegisterView.vue"),
      },
      {
        path: "reports/fixed-assets",
        redirect: "/finance/reports/fixed-assets",
      },
      {
        path: "finance/reports/depreciation",
        name: "DepreciationReport",
        component: () => import("../views/finance/containers/Reports/DepreciationReportView.vue"),
      },
      {
        path: "reports/depreciation",
        redirect: "/finance/reports/depreciation",
      },
      {
        path: "finance/cost-centres",
        name: "CostCentres",
        component: () =>
          import("../views/finance/containers/CostCentres/CostCentreList.vue"),
      },
      {
        path: "finance/cost-centres/create",
        name: "CostCentreCreate",
        redirect: createOnList("/finance/cost-centres"),
      },
      {
        path: "finance/cost-centres/edit/:id",
        name: "CostCentreEdit",
        component: () =>
          import("../views/finance/containers/CostCentres/CostCentreForm.vue"),
        props: true,
      },
      {
        path: "finance/projects",
        name: "Projects",
        component: () =>
          import("../views/finance/containers/Projects/ProjectSiteList.vue"),
      },
      {
        path: "finance/projects/create",
        name: "ProjectCreate",
        redirect: createOnList("/finance/projects"),
      },
      {
        path: "finance/projects/edit/:id",
        name: "ProjectEdit",
        component: () =>
          import("../views/finance/containers/Projects/ProjectSiteForm.vue"),
        props: true,
      },
      {
        path: "finance/cost-allocations",
        name: "CostAllocations",
        component: () =>
          import("../views/finance/containers/CostAllocations/CostAllocationList.vue"),
      },
      {
        path: "finance/cost-allocations/create",
        name: "CostAllocationCreate",
        redirect: createOnList("/finance/cost-allocations"),
      },
      {
        path: "finance/cost-allocations/edit/:id",
        name: "CostAllocationEdit",
        component: () =>
          import("../views/finance/containers/CostAllocations/CostAllocationForm.vue"),
        props: true,
      },
      {
        path: "finance/petty-cash",
        name: "PettyCash",
        component: () => import("../views/finance/containers/PettyCash/PettyCashList.vue"),
      },
      {
        path: "finance/petty-cash/create",
        name: "PettyCashCreate",
        redirect: createOnList("/finance/petty-cash"),
      },
      {
        path: "finance/petty-cash/edit/:id",
        name: "PettyCashEdit",
        component: () => import("../views/finance/containers/PettyCash/PettyCashForm.vue"),
        props: true,
      },
      {
        path: "finance/iou-advances",
        name: "IouAdvances",
        component: () => import("../views/finance/containers/IouAdvances/IouAdvanceList.vue"),
      },
      {
        path: "finance/iou-advances/create",
        name: "IouAdvanceCreate",
        redirect: createOnList("/finance/iou-advances"),
      },
      {
        path: "finance/iou-advances/edit/:id",
        name: "IouAdvanceEdit",
        component: () => import("../views/finance/containers/IouAdvances/IouAdvanceForm.vue"),
        props: true,
      },
      {
        path: "finance/iou-approvals",
        name: "IouApprovals",
        component: () => import("../views/finance/containers/IouApprovals/IouApprovalList.vue"),
      },
      {
        path: "finance/iou-settlements",
        name: "IouSettlements",
        component: () => import("../views/finance/containers/IouSettlements/IouSettlementList.vue"),
      },
      {
        path: "finance/iou-settlements/create",
        name: "IouSettlementCreate",
        redirect: createOnList("/finance/iou-settlements"),
      },
      {
        path: "finance/iou-settlements/edit/:id",
        name: "IouSettlementEdit",
        component: () => import("../views/finance/containers/IouSettlements/IouSettlementForm.vue"),
        props: true,
      },
      {
        path: "finance/iou-outstanding",
        name: "IouOutstanding",
        component: () => import("../views/finance/containers/IouOutstanding/IouOutstandingList.vue"),
      },
      {
        path: "finance/asset-categories",
        name: "AssetCategories",
        component: () => import("../views/finance/containers/AssetCategories/AssetCategoryList.vue"),
      },
      {
        path: "finance/asset-categories/create",
        name: "AssetCategoryCreate",
        redirect: createOnList("/finance/asset-categories"),
      },
      {
        path: "finance/asset-categories/edit/:id",
        name: "AssetCategoryEdit",
        component: () => import("../views/finance/containers/AssetCategories/AssetCategoryForm.vue"),
        props: true,
      },
      {
        path: "finance/fixed-assets",
        name: "FixedAssets",
        component: () => import("../views/finance/containers/FixedAssets/FixedAssetList.vue"),
      },
      {
        path: "finance/fixed-assets/create",
        name: "FixedAssetCreate",
        redirect: createOnList("/finance/fixed-assets"),
      },
      {
        path: "finance/fixed-assets/edit/:id",
        name: "FixedAssetEdit",
        component: () => import("../views/finance/containers/FixedAssets/FixedAssetForm.vue"),
        props: true,
      },
      {
        path: "finance/depreciation",
        name: "DepreciationRuns",
        component: () => import("../views/finance/containers/Depreciation/DepreciationRunList.vue"),
      },
      {
        path: "finance/depreciation/run",
        name: "DepreciationRun",
        redirect: createOnList("/finance/depreciation"),
      },
      {
        path: "finance/depreciation/view/:id",
        name: "DepreciationRunView",
        component: () => import("../views/finance/containers/Depreciation/DepreciationRunForm.vue"),
        props: true,
      },
      {
        path: "finance/tax-classifications",
        name: "TaxClassifications",
        component: () => import("../views/finance/containers/TaxClassifications/TaxClassificationList.vue"),
      },
      {
        path: "finance/tax-classifications/create",
        name: "TaxClassificationCreate",
        redirect: createOnList("/finance/tax-classifications"),
      },
      {
        path: "finance/tax-classifications/edit/:id",
        name: "TaxClassificationEdit",
        component: () => import("../views/finance/containers/TaxClassifications/TaxClassificationForm.vue"),
        props: true,
      },
      {
        path: "finance/tax-rates",
        name: "TaxRates",
        component: () => import("../views/finance/containers/TaxRates/TaxRateList.vue"),
      },
      {
        path: "finance/tax-rates/create",
        name: "TaxRateCreate",
        redirect: createOnList("/finance/tax-rates"),
      },
      {
        path: "finance/tax-rates/edit/:id",
        name: "TaxRateEdit",
        component: () => import("../views/finance/containers/TaxRates/TaxRateForm.vue"),
        props: true,
      },
      {
        path: "finance/tax-account-mappings",
        name: "TaxAccountMappings",
        component: () => import("../views/finance/containers/TaxAccountMappings/TaxAccountMappingList.vue"),
      },
      {
        path: "finance/tax-account-mappings/create",
        name: "TaxAccountMappingCreate",
        redirect: createOnList("/finance/tax-account-mappings"),
      },
      {
        path: "finance/tax-account-mappings/edit/:id",
        name: "TaxAccountMappingEdit",
        component: () => import("../views/finance/containers/TaxAccountMappings/TaxAccountMappingForm.vue"),
        props: true,
      },
      {
        path: "finance/tax-entries",
        name: "TaxEntries",
        component: () => import("../views/finance/containers/TaxEntries/TaxEntryList.vue"),
      },
      {
        path: "finance/tax-entries/create",
        name: "TaxEntryCreate",
        redirect: createOnList("/finance/tax-entries"),
      },
      {
        path: "finance/tax-entries/edit/:id",
        name: "TaxEntryEdit",
        component: () => import("../views/finance/containers/TaxEntries/TaxEntryForm.vue"),
        props: true,
      },
      {
        path: "finance/chart-of-account-types",
        name: "ChartOfAccountTypes",
        component: () =>
          import("../views/finance/containers/ChartOfAccountTypes/ChartOfAccountTypeList.vue"),
      },
      {
        path: "finance/chart-of-account-types/create",
        name: "ChartOfAccountTypeCreate",
        component: () =>
          import("../views/finance/containers/ChartOfAccountTypes/ChartOfAccountTypeForm.vue"),
      },
      {
        path: "finance/chart-of-account-types/edit/:id",
        name: "ChartOfAccountTypeEdit",
        component: () =>
          import("../views/finance/containers/ChartOfAccountTypes/ChartOfAccountTypeForm.vue"),
        props: true,
      },
      {
        path: "finance/chart-of-accounts",
        name: "Chart of Accounts",
        component: () =>
          import("../views/finance/containers/ChartOfAccounts/ChartOfAccountList.vue"),
      },
      {
        path: "finance/chart-of-accounts/create",
        name: "Create Chart of Account",
        component: () =>
          import("../views/finance/containers/ChartOfAccounts/ChartOfAccountForm.vue"),
      },
      {
        path: "finance/chart-of-accounts/edit/:id",
        name: "Edit Chart of Account",
        component: () =>
          import("../views/finance/containers/ChartOfAccounts/ChartOfAccountForm.vue"),
        props: true,
      },
      // Sub Accounts
      {
        path: "finance/sub-accounts",
        name: "Sub Accounts",
        component: () =>
          import("../views/finance/containers/SubAccounts/SubAccountList.vue"),
      },
      {
        path: "finance/sub-accounts/create",
        name: "Create Sub Account",
        component: () =>
          import("../views/finance/containers/SubAccounts/SubAccountForm.vue"),
      },
      {
        path: "finance/sub-accounts/edit/:id",
        name: "Edit Sub Account",
        component: () =>
          import("../views/finance/containers/SubAccounts/SubAccountForm.vue"),
        props: true,
      },
      // Ledger Details
      {
        path: "finance/ledger-details",
        name: "Ledger Details",
        component: () =>
          import("../views/finance/containers/LedgerDetails/LedgerDetailList.vue"),
      },
      {
        path: "finance/ledger-details/create",
        name: "Create Ledger Detail",
        component: () =>
          import("../views/finance/containers/LedgerDetails/LedgerDetailForm.vue"),
      },
      {
        path: "finance/ledger-details/edit/:id",
        name: "Edit Ledger Detail",
        component: () =>
          import("../views/finance/containers/LedgerDetails/LedgerDetailForm.vue"),
        props: true,
      },
      {
        path: "settings/users",
        name: "Finance Users",
        component: () => import("../views/access/UserList.vue"),
      },
      {
        path: "settings/users/create",
        redirect: { path: "/settings/users", query: { create: "1" } },
      },
      {
        path: "settings/users/:id",
        name: "Finance User",
        component: () => import("../views/access/UserForm.vue"),
      },
      {
        path: "settings/roles",
        name: "Finance Roles",
        component: () => import("../views/access/RoleList.vue"),
      },
      {
        path: "settings/roles/create",
        redirect: { path: "/settings/roles", query: { create: "1" } },
      },
      {
        path: "settings/roles/:id",
        name: "Finance Role",
        component: () => import("../views/access/RoleDetail.vue"),
      },
      {
        path: "settings/authorities",
        name: "Finance Authorities",
        component: () => import("../views/access/AuthorityList.vue"),
      },
      {
        path: "settings",
        name: "Settings",
        component: () => import("../views/Settings.vue"),
      },
      {
        path: "forbidden",
        name: "Forbidden",
        component: () => import("../views/Forbidden.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  // Check auth status from localStorage
  authStore.checkAuth();
  const isAuthenticated = authStore.isAuthenticated.value;

  if (to.meta.requiresAuth && !isAuthenticated) {
    next("/login");
    return;
  }

  if (to.meta.requiresGuest && isAuthenticated) {
    next("/");
    return;
  }

  if (isAuthenticated && to.meta.requiresAuth) {
    const permissionStore = usePermissionStore();
    permissionStore.hydrateFromStorage();
    const required = requiredRoutePermissions(to.path);
    if (required && !permissionStore.hasAnyPermission(required) && to.path !== "/forbidden") {
      next({
        path: "/forbidden",
        query: { required: required.join(",") },
        replace: true,
      });
      return;
    }
  }

  next();
});

export default router;
